import { TwoFactorAuthenticationModal, useCustomerVerification } from '@components';
import { VerificationMessageType } from '@models';
import { Box, Button, Divider, Modal, Typography } from '@mui/material';
import { useCustomer } from '@hooks/useCustomer';
import { useGenerateOneTimePasswordMutation, useVerifyOneTimePasswordMutation } from '@services';
import { useState } from 'react';

export const CustomerVerification = () => {
  const [messageTypeModal, setMessageTypeModal] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [code, setCode] = useState('');
  const [verificationError, setVerificationError] = useState<{ hasError: boolean; errorDesc: string }>({ hasError: false, errorDesc: '' });

  const { customer } = useCustomer();
  const { verified, checkVerificationStatus } = useCustomerVerification();

  const [generateOneTimePassword] = useGenerateOneTimePasswordMutation();
  const [verifyOneTimePassword] = useVerifyOneTimePasswordMutation();

  const sendOneTimePassword = (messageType: VerificationMessageType) => {
    setMessageTypeModal(false);
    generateOneTimePassword({ userId: customer.id, messageType });
    setAuthModalOpen(true);
  };

  const existingPassword = () => {
    setMessageTypeModal(false);
    setAuthModalOpen(true);
  };

  const verify = () => {
    verifyOneTimePassword({ userId: customer.id, otp: code })
      .unwrap()
      .then((res) => {
        if (res.data) {
          checkVerificationStatus();
          setAuthModalOpen(false);
        }

        if (res.code !== 200 || !res.data) {
          setVerificationError({ hasError: true, errorDesc: res.message ?? 'Error verifying customer OTP' });
        }
      });
  };

  const onClose = () => {
    setAuthModalOpen(false);
    setCode('');
    setVerificationError({ hasError: false, errorDesc: '' });
  };

  return (
    <>
      {verified ? (
        <Button variant="outlined" color="success" data-testid="customer-verified-btn">
          Authenticated
        </Button>
      ) : (
        <Button variant="outlined" onClick={() => setMessageTypeModal(true)} data-testid="customer-verification-btn">
          Authenticate Customer
        </Button>
      )}
      <Modal
        data-testid="otp-message-type-modal"
        onClose={() => setMessageTypeModal(false)}
        open={messageTypeModal}
        style={{ alignContent: 'center', justifyContent: 'center', display: 'flex', flexWrap: 'wrap' }}
      >
        <Box display="flex" bgcolor="white" borderRadius="10px" flexDirection="column" p={8} gap={2}>
          <Typography variant="h3" textAlign={'center'}>
            Send OTP Verification Code
          </Typography>
          <Divider />
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography textAlign={'center'}>Where would you like to send the customer their one-time password?</Typography>
            <Box display="flex" gap={2} justifyContent="center">
              <Button
                onClick={() => sendOneTimePassword('Email')}
                data-testid="otp-message-type-email-btn"
                variant="contained"
                size="large"
                fullWidth
              >
                Email
              </Button>
              <Divider orientation="vertical" />
              <Button
                onClick={() => sendOneTimePassword('SMS')}
                data-testid="otp-message-type-sms-btn"
                variant="contained"
                size="large"
                fullWidth
              >
                SMS
              </Button>
            </Box>
            <Divider />
            <Button onClick={existingPassword} data-testid="verify-existing-otp-btn" variant="contained" size="large" fullWidth>
              Verify existing OTP
            </Button>
          </Box>
        </Box>
      </Modal>
      <TwoFactorAuthenticationModal
        isErrorCode={verificationError.hasError}
        errorDesc={verificationError.errorDesc}
        onSubmit={verify}
        open={authModalOpen}
        onClose={onClose}
        fieldValue={code}
        onInputChange={(e) => setCode(e.target.value)}
        modalTitle="Customer OTP Verification"
        modalDescription="One-time password has been sent to the customer. Please verify the password here."
      />
    </>
  );
};
